// externals
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import type { DealHistory as DealHistoryType } from '@makemydeal/dr-dash-types';
import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

// components
import {
    adjustOfferCardHeights,
    ExpandableAreaProvider,
    OfferCardInterstate,
    OfferCompareColumnInterstate,
    SliderCarousel
} from '@makemydeal/dr-dash-components';
import DealHistoryFooterInterstate from './dealHistory/footerContainer/DealHistoryFooter.interstate';

// selectors
import * as dealHistoryActionCreators from '@makemydeal/dr-dash-store/actions/dealHistoryActionCreator';
import * as dealHistorySelectors from '@makemydeal/dr-dash-store/selectors/dealHistorySelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';

// styling
import { StyledSliderCarouselContainer } from './dealHistory/DealHistory.interstate.style';
import { StyledColumnsContainer, OfferCompareContainer } from './DealHistoryActivity.interstate.style';

type DealHistoryActivityInterstateProps = {
    onClose?: () => void;
    isInDrawer?: boolean;
};

const DealHistoryActivityInterstate = ({ onClose, isInDrawer }: DealHistoryActivityInterstateProps = {}) => {
    const dispatch = useDispatch();
    const theme = useInterstateTheme();
    const deals = useSelector(dealHistorySelectors.getDealHistoryOffers);
    const workingOffer = useSelector(offerSelectors.getWorkingOffer);
    const selectedDeals = useSelector(dealHistorySelectors.getSelectedDealHistory);
    const defaultOffer = useSelector(dealHistorySelectors.getDefaultDealHistory);
    const mostRecentDealerOffer = useSelector(dealHistorySelectors.getLatestDealerOffer);
    const mostRecentShopperOffer = useSelector(dealHistorySelectors.getLatestShopperOffer);
    const isDealHistoryLoading = useSelector(dealHistorySelectors.getDealHistoryIsLoading);
    const hasAutoSelected = useRef(false);

    // Auto-select target version for comparison when navigating from "Review now" CTA
    useEffect(() => {
        if (isDealHistoryLoading || hasAutoSelected.current) return;

        const params = new URLSearchParams(window.location.search);
        const targetVersion = params.get('targetDealXgVersion');
        if (!targetVersion) return;

        // Don't override existing manual selection
        if (selectedDeals.length > 0) {
            hasAutoSelected.current = true;
            return;
        }

        const matchingDeal = deals.find((deal) => deal.offer.dealXgVersion === targetVersion);
        if (!matchingDeal) return;

        hasAutoSelected.current = true;
        dispatch(dealHistoryActionCreators.dealHistoryChecked(matchingDeal));
        dispatch(dealHistoryActionCreators.dealHistorySelectedUpdate(matchingDeal.offer.offerId as string));
    }, [isDealHistoryLoading, deals, selectedDeals.length, dispatch]);

    const isDefaultOffer = (deal: DealHistoryType) => {
        return deal.offer.offerId === defaultOffer.offer?.offerId;
    };

    const isMostRecentDealerOffer = (deal: DealHistoryType) => {
        return deal.offer.offerId === mostRecentDealerOffer?.offer?.offerId;
    };

    const isMostRecentShopperOffer = (deal: DealHistoryType) => {
        return deal.offer.offerId === mostRecentShopperOffer?.offer?.offerId;
    };

    const onDealClick = (deal: DealHistoryType) =>
        dispatch(dealHistoryActionCreators.dealHistorySelectedUpdate(deal.offer.offerId as string));

    useLayoutEffect(() => {
        adjustOfferCardHeights('#deal-history-offer-cards-carousel-scroller');
    }, [deals, defaultOffer, mostRecentDealerOffer, mostRecentShopperOffer]);

    return (
        <Grid pb="104px">
            {!isInDrawer && (
                <Box paddingTop={5} paddingBottom={2}>
                    <Typography variant="h1" id="deal-history-page-title" data-testid="deal-history-page-title">
                        View and Compare Offer History
                    </Typography>
                </Box>
            )}

            <StyledSliderCarouselContainer theme={theme}>
                <SliderCarousel id="deal-history-offer-cards-carousel">
                    {deals.map((deal) => (
                        <OfferCardInterstate
                            deal={deal}
                            isDealHistory={true}
                            key={deal.offer.offerId}
                            isDefaultOffer={isDefaultOffer(deal)}
                            isMostRecentDealerOffer={isMostRecentDealerOffer(deal)}
                            isMostRecentShopperOffer={isMostRecentShopperOffer(deal)}
                        />
                    ))}
                </SliderCarousel>
            </StyledSliderCarouselContainer>

            <ExpandableAreaProvider>
                <OfferCompareContainer cardsAmount={selectedDeals.length + 1}>
                    <StyledColumnsContainer data-testid={'current-offer-compare-column'} key={'offer-wrapper-main'}>
                        <OfferCompareColumnInterstate deal={workingOffer} isCurrent={true} isDealHistory={true} />
                    </StyledColumnsContainer>
                    {selectedDeals &&
                        selectedDeals.map((deal) => (
                            <StyledColumnsContainer
                                isSelected={deal.isSelected}
                                onClick={() => onDealClick(deal)}
                                data-testid={'selected-offer-compare-column'}
                                key={'offer-wrapper' + deal.offer.offerId}
                            >
                                <OfferCompareColumnInterstate
                                    isCurrent={false}
                                    deal={deal}
                                    isDealHistory={true}
                                    isDefaultOffer={isDefaultOffer(deal)}
                                    isMostRecentDealerOffer={isMostRecentDealerOffer(deal)}
                                    isMostRecentShopperOffer={isMostRecentShopperOffer(deal)}
                                    scenarioCount={dealHistorySelectors.getDealScenarioCount(deal) || 0}
                                />
                            </StyledColumnsContainer>
                        ))}
                </OfferCompareContainer>
            </ExpandableAreaProvider>
            <DealHistoryFooterInterstate onClose={onClose} $isInDrawer={!!onClose} />
        </Grid>
    );
};

export default DealHistoryActivityInterstate;
